import type { FC } from 'react'
import { useDeferredValue, useState } from 'react'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useIsFetching } from '@tanstack/react-query'

import { useDebounce } from 'react-use'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import NavigationV2 from '@segment/templates/Navigation2'
import Footer from '@segment/templates/Footer/Footer'
import GettingStarted from '@segment/templates/GettingStarted'

import { useFeaturedResourcesQuery } from '@segment/api/routes/resources/queries'

import useAlgoliaFilters from '@segment/utils/useAlgoliaFilters'

import Layout from '@segment/next-shared/layout'

import AdditionalResources from '../components/AdditionalResources'
import ResourcesDivider from '../components/ResourcesDivider'

import Hero from './components/Hero'
import Filters from './components/Filters'
import FeaturedResources from './components/FeaturedResources'
import useResourcesGrid from './components/ResourcesGrid/useResourcesGrid'
import ResourcesGrid from './components/ResourcesGrid'

import ogImagePng from './assets/OgResources.png'

const copy = {
  pageTitle: `Twilio Segment Resources`,
  pageDescription: `Segment is a customer data platform (CDP) that helps you collect, clean, and activate your customer data.`,
}

const getCardsSectionTitle = (query: string, filtersString: string) => {
  if (query === `` && filtersString === ``) {
    return `All resources`
  }

  if (query === ``) {
    return null
  }

  return `Search results for "${query}" in resources`
}

const ResourcesView: FC = () => {
  const theme = useTheme()
  const [query, setQuery] = useState(``)
  const deferredQuery = useDeferredValue(query)
  const [debouncedQuery, setDebouncedQuery] = useState(query)
  useDebounce(() => setDebouncedQuery(query), 200, [query])

  const ongoingFetches = useIsFetching({ queryKey: [`resources`] })
  const isQueryLoading = ongoingFetches > 0
  const { updateFilters, filtersString } = useAlgoliaFilters()

  const { data: featuredResources } = useFeaturedResourcesQuery({
    args: [{ max: 3 }],
  })

  const { flattenedPages, isEmpty, hasNextPage, fetchNextPage } = useResourcesGrid({
    query: debouncedQuery,
    filters: filtersString,
  })

  const showFeaturedResources = deferredQuery === `` && filtersString === `` && featuredResources

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle={copy.pageTitle}
        pageDescription={copy.pageDescription}
        pageOG={ogImagePng}
        bodyBackground={theme.palette.neutrals.white[100]}
        themeColor={theme.palette.neutrals.white[100]}
      >
        <NavigationV2 />
        <Hero query={query} onQueryChange={setQuery} isQueryLoading={isQueryLoading} />
        <Main>
          <Filters onFilterChange={updateFilters} />
          {showFeaturedResources && (
            <>
              <FeaturedResources resources={featuredResources} />
              <ResourcesDivider />
            </>
          )}
          <ResourcesGrid
            title={getCardsSectionTitle(deferredQuery, filtersString)}
            resources={flattenedPages}
            isEmpty={isEmpty}
            isLoading={isQueryLoading}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
          />
          {isEmpty && (
            <>
              <ResourcesDivider />
              <FeaturedResources resources={featuredResources} />
            </>
          )}
          <ResourcesDivider />
          <AdditionalResources />
          <GettingStarted />
        </Main>
        <Footer />
      </Layout>
    </AppearanceProvider>
  )
}

export default ResourcesView

const Main = styled.main``
